import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch } from "@phosphor-icons/react";

export default function ReassignDialog({ open, prospect, users, onClose, onDone }) {
  const [salespersonId, setSalespersonId] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/prospects/${prospect.id}/reassign`, { salesperson_id: salespersonId, note: note || null });
      toast.success("Prospect reassigned — full history preserved");
      setSalespersonId("");
      setNote("");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Reassign Prospect</DialogTitle>
          <DialogDescription>
            Administrator-only. The complete ownership history is preserved; the Lead Originator never changes.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">New assigned salesperson *</Label>
            <Select value={salespersonId} onValueChange={setSalespersonId} required>
              <SelectTrigger className="rounded-sm" data-testid="reassign-salesperson"><SelectValue placeholder="Select user" /></SelectTrigger>
              <SelectContent>
                {(users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Reassignment note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-sm" data-testid="reassign-note" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading || !salespersonId} className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#032852]/90" data-testid="submit-reassign">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Reassign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
